import { makeAutoObservable, observable, runInAction } from "mobx";
import { agent } from "../api/agent";
import { toast } from "react-toastify";
import { CompanySportVenue } from "../models/CompanySportVenue";
import i18n from "../../i18n";


export default class CompanySportVenueStore {
    venueRegistry = new Map<string, CompanySportVenue>();
    selectedVenue: CompanySportVenue | undefined = undefined;
    editMode = false;
    loading = false;
    loadingInitial = false;
    predicate = new Map();

    constructor() {
        makeAutoObservable(this);
    }

    get venues() {
        return Array.from(this.venueRegistry.values());
    }

    loadCompanyVenues = async (companyId: string) => {
        this.setLoadingInitial(true);
        try {
            const venues = await agent.CompanySportVenues.listByCompany(companyId);
            runInAction(() => {
                this.venueRegistry.clear();
                venues.forEach(venue => {
                    this.setVenue(venue);
                });
                this.setLoadingInitial(false);
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.setLoadingInitial(false);
            });
            toast.error((i18n as any).t('venue.errors.loadingVenues'));
        }
    };

    loadAllVenues = async () => {
        this.setLoadingInitial(true);
        try {
            const venues = await agent.CompanySportVenues.list();
            runInAction(() => {
                this.venueRegistry.clear();
                venues.forEach(venue => {
                    this.setVenue(venue);
                });
                this.setLoadingInitial(false);
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.setLoadingInitial(false);
            });
            toast.error((i18n as any).t('venue.errors.loadingVenues'));
        }
    };

    loadVenue = async (id: string) => {
        let venue = this.getVenue(id);
        if (venue) {
            this.selectedVenue = venue;
            return venue;
        } else {
            this.loadingInitial = true;
            try {
                venue = await agent.CompanySportVenues.details(id);
                runInAction(() => {
                    this.setVenue(venue!);
                    this.selectedVenue = venue;
                    this.loadingInitial = false;
                });
                return venue;
            } catch (error) {
                console.log(error);
                runInAction(() => {
                    this.loadingInitial = false;
                });
                toast.error((i18n as any).t('venue.errors.loadingVenueDetails'));
            }
        }
    };

    createVenue = async (venue: CompanySportVenue) => {
        this.loading = true;
        try {
            venue.id = '00000000-0000-0000-0000-000000000000'; // Placeholder ID
            const createdVenue = await agent.CompanySportVenues.create(venue);         
            runInAction(() => {
                this.venueRegistry.set(createdVenue.id, createdVenue);
                this.selectedVenue = createdVenue;
                this.editMode = false;
                this.loading = false;
            });
            toast.success((i18n as any).t('venue.success.venueCreated'));
            return createdVenue;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('venue.errors.creatingVenue'));
            return undefined;
        }
    };

    updateVenue = async (venue: CompanySportVenue) => {
        this.loading = true;
        try {
            const updatedVenue = await agent.CompanySportVenues.update(venue);
            runInAction(() => {
                this.setVenue(updatedVenue);
                this.selectedVenue = updatedVenue;
                this.editMode = false;
                this.loading = false;
            });
            toast.success((i18n as any).t('venue.success.venueUpdated'));
            return updatedVenue;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('venue.errors.updatingVenue'));
            return undefined;
        }
    };

    deleteVenue = async (id: string) => {
        this.loading = true;
        try {
            await agent.CompanySportVenues.delete(id);
            runInAction(() => {
                this.venueRegistry.delete(id);
                this.loading = false;
            });
            toast.success((i18n as any).t('venue.success.venueDeleted'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('venue.errors.deletingVenue'));
        }
    };

    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    private getVenue = (id: string) => {
        return this.venueRegistry.get(id);
    };

    private setVenue = (venue: CompanySportVenue) => {
        // Create a deep observable copy to ensure all nested properties are tracked
        const observableVenue = observable({
            ...venue,
            sportVenueWorkingHours: venue.sportVenueWorkingHours ? 
                observable(venue.sportVenueWorkingHours.map(wh => observable(wh))) : 
                []
        });
        
        this.venueRegistry.set(venue.id, observableVenue);
    };

    get axiosParams() {
        const params = new URLSearchParams();

        this.predicate.forEach((value, key) => {
            params.append(key, value.toString());
        });

        return params;
    };
}